import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import type { DataTablePageEvent } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Skeleton } from 'primereact/skeleton';
import DrugEstablishmentModal from './DrugEstablishmentModal';
import { productAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import { LabelerInformationItem } from 'Interfaces/ProductWikiInterfaces/labelerInformation.types';
import { CBP_CROSS_ORIGIN_BASE } from '../../../../../../../../constants';
import './LabelerInformationTable.styles.scss';

function openCrossOriginUrl(moleculeName: string): void {
  const term = encodeURIComponent(moleculeName.trim());
  const url = CBP_CROSS_ORIGIN_BASE.replace('{term}', term);
  window.open(url, '_blank', 'noopener,noreferrer');
}

function parseMoleculesFromItem(itemParam: string | null): string[] {
  if (!itemParam || !itemParam.trim()) return [];
  return itemParam
    .split('/')
    .map((s) => s.trim())
    .filter(Boolean);
}

const PAGE_SIZE = 10;
const TAA_PAGE_SIZE = 20;
const DER_PAGE_SIZE = 20;
const TAA_SEARCH_DEBOUNCE_MS = 350;
const LABELER_SEARCH_DEBOUNCE_MS = 350;

const LabelerInformationTable: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [showTAAModal, setShowTAAModal] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [labelerSearchTerm, setLabelerSearchTerm] = useState('');
  const [showEstablishmentModal, setShowEstablishmentModal] = useState(false);
  const [showCrossOriginDropdown, setShowCrossOriginDropdown] = useState(false);
  const taaSearchFirstOpenRef = useRef(true);
  const labelerSearchFirstRunRef = useRef(true);
  const crossOriginDropdownRef = useRef<HTMLDivElement>(null);

  const molecules = useMemo(
    () => parseMoleculesFromItem(searchParams.get('item')),
    [searchParams]
  );

  const {
    labelerInformationData,
    isLabelerInformationLoading,
    labelerInformationError,
    currentGfc,
    fetchLabelerInformation,
    taaDesignatedCountriesData,
    isTaaDesignatedCountriesLoading,
    taaDesignatedCountriesError,
  } = productAnalysisStore;

  const list = labelerInformationData?.labeler_information ?? [];
  const totalCount = labelerInformationData?.total_count ?? 0;
  const currentPage = labelerInformationData?.page ?? 1;
  const pageSize = labelerInformationData?.page_size ?? PAGE_SIZE;

  useEffect(() => {
    const gfc = currentGfc?.trim();
    if (!gfc) return;
    if (labelerSearchFirstRunRef.current) {
      labelerSearchFirstRunRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      fetchLabelerInformation({
        gfc,
        page: 1,
        page_size: PAGE_SIZE,
        search: labelerSearchTerm.trim() || undefined,
      });
    }, LABELER_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelerSearchTerm, currentGfc]);

  const handlePageChange = (event: DataTablePageEvent) => {
    const gfc = currentGfc?.trim();
    if (!gfc) return;
    const page = Math.floor(event.first / event.rows) + 1;
    fetchLabelerInformation({
      gfc,
      page,
      page_size: event.rows,
      search: labelerSearchTerm.trim() || undefined,
    });
  };

  const getFlagEmoji = (code: string): string => {
    const flags: { [key: string]: string} = {
      'US': '🇺🇸', 'GB': '🇬🇧', 'IN': '🇮🇳', 'DE': '🇩🇪', 'CN': '🇨🇳',
      'FR': '🇫🇷', 'CA': '🇨🇦', 'AU': '🇦🇺', 'JP': '🇯🇵', 'MX': '🇲🇽'
    };
    return flags[code] || '🏳️';
  };

  const labelerTemplate = (rowData: LabelerInformationItem) => {
    return (
      <div className="labeler-cell">
        <div className="labeler-name">{rowData.labeler}</div>
      </div>
    );
  };

  const labelerCountryTemplate = (rowData: LabelerInformationItem) => {
    return (
      <div className="country-cell">
        <span className="flag">{getFlagEmoji(rowData.labeler_country_code)}</span>
        <span>{rowData.labeler_country}</span>
      </div>
    );
  };

  const cooTemplate = (rowData: LabelerInformationItem) => {
    const coo = rowData.coo ?? '';
    const cooCode = rowData.coo_code ?? '';
    return (
      <div className="country-cell">
        <span className="flag">{getFlagEmoji(cooCode)}</span>
        <span>{coo || '—'}</span>
      </div>
    );
  };

  const countriesList = taaDesignatedCountriesData?.countries ?? [];
  const taaPage = taaDesignatedCountriesData?.page ?? 1;
  const taaPageSize = taaDesignatedCountriesData?.page_size ?? TAA_PAGE_SIZE;
  const taaTotalCount = taaDesignatedCountriesData?.total_count ?? 0;

  const handleTaaPageChange = (event: DataTablePageEvent) => {
    const page = Math.floor(event.first / event.rows) + 1;
    productAnalysisStore.fetchTaaDesignatedCountries({
      page,
      page_size: event.rows,
      search: countrySearch.trim() || undefined,
    });
  };

  useEffect(() => {
    if (!showTAAModal) return;
    if (taaSearchFirstOpenRef.current) {
      taaSearchFirstOpenRef.current = false;
      return;
    }
    const timer = setTimeout(() => {
      productAnalysisStore.fetchTaaDesignatedCountries({
        page: 1,
        page_size: TAA_PAGE_SIZE,
        search: countrySearch.trim() || undefined,
      });
    }, TAA_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [countrySearch, showTAAModal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showCrossOriginDropdown &&
        crossOriginDropdownRef.current &&
        !crossOriginDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCrossOriginDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCrossOriginDropdown]);

  const renderLabelerSectionContent = () => {
    if (isLabelerInformationLoading && !labelerInformationData) {
      return (
        <div className="labeler-info-skeleton">
          <div className="labeler-info-skeleton__header">
            {['Labeler', 'Labeler Country', 'Manufacturers', 'Country of Origin'].map(
              (_, i) => (
                <Skeleton key={i} width="100%" height="2rem" borderRadius="4px" />
              )
            )}
          </div>
          {Array.from({ length: 4 }).map((_, rowIndex) => (
            <div key={rowIndex} className="labeler-info-skeleton__row">
              <Skeleton width="100%" height="1.5rem" borderRadius="4px" />
              <Skeleton width="90%" height="1.5rem" borderRadius="4px" />
              <Skeleton width="95%" height="1.5rem" borderRadius="4px" />
              <Skeleton width="85%" height="1.5rem" borderRadius="4px" />
            </div>
          ))}
        </div>
      );
    }
    if (labelerInformationError && !labelerInformationData) {
      return (
        <div className="labeler-info-message labeler-info-message--error">
          <i className="pi pi-exclamation-triangle labeler-info-message__icon" />
          <p className="labeler-info-message__text">{labelerInformationError}</p>
        </div>
      );
    }
    return (
      <DataTable
        value={list}
        className="labeler-info-table"
        stripedRows
        emptyMessage="No labeler data available"
        lazy
        paginator
        first={(currentPage - 1) * pageSize}
        rows={pageSize}
        totalRecords={totalCount}
        onPage={handlePageChange}
        loading={isLabelerInformationLoading}
      >
            <Column
              header="Labeler"
              body={labelerTemplate}
              style={{ minWidth: '250px' }}
            />
            <Column
              header="Labeler Country"
              body={labelerCountryTemplate}
              style={{ width: '180px' }}
            />
            <Column
              field="manufacturer"
              header="Manufacturers"
              style={{ minWidth: '220px' }}
            />
            <Column
              header="Country of Origin"
              body={cooTemplate}
              style={{ width: '200px' }}
            />
          </DataTable>
    );
  };

  return (
    <>
      <section className="narrative-box labeler-info-box">
        <div className="section-header-with-action">
          <h4 className="narrative-box-text-title">
            <i className="pi pi-building" style={{ marginRight: '0.5rem' }} />
            Labeler Information
          </h4>
          <div className="labeler-info-header-actions">
            <IconField iconPosition="left">
              <InputIcon className="pi pi-search" />
              <InputText
                value={labelerSearchTerm}
                onChange={(e) => setLabelerSearchTerm(e.target.value)}
                placeholder="Search labeler, country…"
                className="labeler-info-search-input"
              />
            </IconField>
            <Button
              label="View Compliant Countries"
              icon="pi pi-globe"
              size="small"
              outlined
              onClick={() => {
                setShowTAAModal(true);
                taaSearchFirstOpenRef.current = true;
                productAnalysisStore.fetchTaaDesignatedCountries({
                  page: 1,
                  page_size: TAA_PAGE_SIZE,
                });
              }}
            />
            {molecules.length > 0 &&
              (molecules.length === 1 ? (
                <Button
                  label="Cross Origin"
                  icon="pi pi-external-link"
                  size="small"
                  outlined
                  title={`Open CBP Cross Origin search for ${molecules[0].trim()} in new tab`}
                  onClick={() => openCrossOriginUrl(molecules[0])}
                />
              ) : (
                <div
                  className="cross-origin-dropdown-container"
                  ref={crossOriginDropdownRef}
                >
                  <Button
                    label="Cross Origin"
                    icon={`pi ${showCrossOriginDropdown ? 'pi-chevron-up' : 'pi-chevron-down'}`}
                    iconPos="right"
                    size="small"
                    outlined
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowCrossOriginDropdown((prev) => !prev);
                    }}
                  />
                  {showCrossOriginDropdown && (
                    <div className="cross-origin-dropdown">
                      {molecules.map((molecule) => (
                        <button
                          key={molecule}
                          type="button"
                          className="cross-origin-dropdown-item"
                          onClick={(e) => {
                            e.stopPropagation();
                            openCrossOriginUrl(molecule);
                            setShowCrossOriginDropdown(false);
                          }}
                        >
                          <i className="pi pi-external-link" />
                          {molecule}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            <Button
              label="Search Establishments"
              icon="pi pi-search"
              size="small"
              outlined
              onClick={() => {
                setShowEstablishmentModal(true);
                productAnalysisStore.clearDrugEstablishmentsData();
                productAnalysisStore.fetchDrugEstablishments({
                  gfc: productAnalysisStore.currentGfc ?? '',
                  page: 1,
                  page_size: DER_PAGE_SIZE,
                });
              }}
            />
          </div>
        </div>

        <div className="table-container">
          {renderLabelerSectionContent()}
        </div>

      </section>

      {/* TAA Compliant Countries Modal */}
      <Dialog
        header="TAA Designated Countries"
        visible={showTAAModal}
        style={{ width: '900px' }}
        onHide={() => {
          setShowTAAModal(false);
          setCountrySearch('');
          taaSearchFirstOpenRef.current = true;
        }}
        modal
      >
        <div className="taa-modal-content">
          <div className="modal-header-info">
            <p className="total-count">
              {isTaaDesignatedCountriesLoading && !taaDesignatedCountriesData ? (
                'Loading...'
              ) : (
                <>
                  <strong>{taaTotalCount}</strong> TAA-designated countries
                </>
              )}
            </p>
            {!isTaaDesignatedCountriesLoading && (
              <span className="p-input-icon-left">
                {/* <i className="pi pi-search" /> */}
                <InputText
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  placeholder="Search countries..."
                  style={{ width: '250px' }}
                />
              </span>
            )}
          </div>

          {isTaaDesignatedCountriesLoading && !taaDesignatedCountriesData && (
            <div className="taa-modal-skeleton">
              <div className="taa-modal-skeleton__header">
                <Skeleton width="100%" height="1.75rem" borderRadius="4px" />
                <Skeleton width="5rem" height="1.75rem" borderRadius="4px" />
                <Skeleton width="5rem" height="1.75rem" borderRadius="4px" />
                <Skeleton width="5rem" height="1.75rem" borderRadius="4px" />
              </div>
              {Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="taa-modal-skeleton__row">
                  <Skeleton width="100%" height="1.5rem" borderRadius="4px" />
                  <Skeleton width="4rem" height="1.5rem" borderRadius="4px" />
                  <Skeleton width="4rem" height="1.5rem" borderRadius="4px" />
                  <Skeleton width="4rem" height="1.5rem" borderRadius="4px" />
                </div>
              ))}
            </div>
          )}

          {taaDesignatedCountriesError && !taaDesignatedCountriesData && (
            <div className="labeler-info-message labeler-info-message--error">
              <i className="pi pi-exclamation-triangle labeler-info-message__icon" />
              <p className="labeler-info-message__text">{taaDesignatedCountriesError}</p>
            </div>
          )}

          {taaDesignatedCountriesData && (
            <>
              <DataTable
                value={countriesList}
                lazy
                stripedRows
                scrollable
                scrollHeight="500px"
                paginator
                totalRecords={taaTotalCount}
                first={(taaPage - 1) * taaPageSize}
                rows={taaPageSize}
                onPage={handleTaaPageChange}
                loading={isTaaDesignatedCountriesLoading}
                emptyMessage="No countries found"
              >
                <Column
                  field="country"
                  header="Country"
                  style={{ minWidth: '250px', fontWeight: 500 }}
                />
                <Column
                  field="alpha_2"
                  header="ISO Alpha-2"
                  style={{ width: '120px', fontFamily: 'monospace' }}
                />
                <Column
                  field="alpha_3"
                  header="ISO Alpha-3"
                  style={{ width: '120px', fontFamily: 'monospace' }}
                />
                <Column
                  field="numeric"
                  header="Numeric Code"
                  style={{ width: '120px', fontFamily: 'monospace' }}
                />
              </DataTable>

              {/* <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #dee2e6', color: '#6c757d', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="pi pi-info-circle" style={{ color: '#6366f1' }} />
                <span>Source: Trade Agreements Act (TAA) Designated Countries (pre_stg.TAADesignatedCountries)</span>
              </div> */}
            </>
          )}
        </div>
      </Dialog>

      {/* Drug Establishment Registration Modal */}
      <DrugEstablishmentModal
        visible={showEstablishmentModal}
        onHide={() => setShowEstablishmentModal(false)}
      />
    </>
  );
};

export default observer(LabelerInformationTable);
